import {combineLatest as observableCombineLatest} from 'rxjs';

import {map, takeWhile, catchError} from 'rxjs/operators';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, NgZone} from '@angular/core';
import {AbstractControlComponent} from './abstract-control';
import {ViewTabComponent} from '../viewtab.component';
import {multivalueRequired} from './validators/multivalue-required-validator';
import {ErrorService} from '../../../error/error.service';
import {ValidatorFn, Validators} from '@angular/forms';

@Component({
  selector: 'ctrl-commonmultivalue',
  template: `
    @if (getCaughtObservable(isEnabled) | async) {
      <div class="add">
        <mat-form-field floatLabel="never">
          <textarea
            matInput
            matTextareaAutosize
            [(ngModel)]="inputValue"
            (keypress)="onKeyTyped($event)"
            rows="1"
          ></textarea>
          <div matSuffix>
            <mat-icon (click)="onAddClick()">subdirectory_arrow_left</mat-icon>
          </div>
        </mat-form-field>
        @if (formControl.touched && formControl.hasError('required')) {
          <mat-error>
            <language key="ControlErrorRequired" defaultValue="This field is required."></language>
          </mat-error>
        }
      </div>
    }
    <div class="preview">
      <mat-chip-set class="chip-preview-list">
        @for (val of chipList; track val; let i = $index) {
          <mat-chip (removed)="removeValueAt(i)">
            {{ val }}
            @if (getCaughtObservable(isEnabled) | async) {
              <button matChipRemove [attr.aria-label]="'remove ' + val">
                <mat-icon>cancel</mat-icon>
              </button>
            }
          </mat-chip>
        }
        @if (remainingValueLength > 0) {
          <mat-chip class="chip-remaining chip-clickable" (click)="onRemainingClick()">
            <language
              key="CtrlCommonMultiValueMoreItems"
              defaultValue="… and {0} more."
              [args]="[remainingValueLength.toString()]"
            ></language>
          </mat-chip>
        }
      </mat-chip-set>
      @if ((getCaughtObservable(isEnabled) | async) && formControl.value?.length > 0) {
        <button mat-icon-button class="clear" (click)="clear()">
          <mat-icon>delete_sweep</mat-icon>
        </button>
      }
    </div>
  `,
  styleUrls: ['./ctrl-commonmultivalue.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class CtrlCommonMultiValueControlComponent extends AbstractControlComponent {
  inputValue: string = '';
  private dataType: string = 'String';

  private previewCount: number = null;
  remainingValueLength: number = 0;

  constructor(
    parent: ViewTabComponent,
    changeDetector: ChangeDetectorRef,
    ngZone: NgZone,
    errorService: ErrorService,
  ) {
    super(parent, 'Value', 'Value', changeDetector, ngZone, errorService);
  }

  protected initValidators(): void {
    if (this._config == null || this.formControl == null) {
      throw Error('Initialized target too early.');
    }

    observableCombineLatest(
      this._config.getBooleanPropertyValues('Required'),
      this._config.getPropertyValues('RegularExpression'),
      (required: boolean, regex: string) => {
        const validators: ValidatorFn[] = [];

        if (required === true) {
          validators.push(multivalueRequired());
        }

        if (regex) {
          validators.push(Validators.pattern(regex));
        }

        this.formControl.setValidators(validators);
        this.formControl.updateValueAndValidity({emitEvent: false});
      },
    )
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
      )
      .subscribe();

    this._config
      .getPropertyValues('DataType')
      .pipe(
        catchError((error, caught) => this.handleError(error, caught)),
        takeWhile(() => this.alive),
      )
      .subscribe((type: string) => {
        if (type == null) {
          // required attribute not fulfilled
          this.dataType = 'String';
          return;
        }

        const compareType: string = type.trim().toLowerCase();
        if (compareType === 'integer') {
          this.dataType = 'Integer';
        } else if (compareType === 'datetime') {
          this.dataType = 'DateTime';
        } else {
          this.dataType = 'String';
        }
      });

    if (this._config.propertyExists('PreviewCount')) {
      this._config
        .getPropertyValues('PreviewCount')
        .pipe(
          map(s => Number(s)),
          catchError((error, caught) => this.handleError(error, caught)),
          takeWhile(() => this.alive),
        )
        .subscribe((c: number) => {
          this.previewCount = c;
          this.changeDetector.markForCheck();
        });
    }
  }

  onKeyTyped(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.onAddClick();
      event.preventDefault();
      event.stopPropagation();
    }
  }

  onAddClick(): void {
    const val: string[] = this.inputValue.replace(/\r/g, '').split('\n');
    if (val != null && val.length !== 0) {
      val.forEach((v: string) => {
        v = v.trim();
        if (v !== '') {
          this.addValue(v);
        }
      });
    }
    this.inputValue = '';
    this.formControl.markAsTouched();
    this.formControl.updateValueAndValidity({emitEvent: true});
    this.changeDetector.markForCheck();
  }

  private addValue(value: string): void {
    let storeVal: any = value;
    if (this.dataType === 'Integer') {
      storeVal = Number(value);
      if (isNaN(storeVal) || storeVal == null || !Number.isInteger(storeVal)) {
        return;
      }
    } else if (this.dataType === 'DateTime') {
      // TODO: how to handle dates?
    }
    if (this.formControl.value.indexOf(storeVal) === -1) {
      this.formControl.value.push(storeVal);
      this.formControl.updateValueAndValidity({emitEvent: true});
    }
  }

  removeValueAt(index: number): void {
    if (this.formControl.value.length <= index) return;
    this.formControl.value.splice(index, 1);
    this.formControl.markAsTouched();
    this.formControl.updateValueAndValidity({emitEvent: true});
  }

  clear(): void {
    this.formControl.setValue([], {emitEvent: true});
  }

  protected formatSource(source: any): any {
    if (source == null) return [];
    return source;
  }

  protected formatTarget(value: any): any {
    if (Array.isArray(value) && value.length === 0) return null;
    return value;
  }

  get chipList(): any[] {
    if (this.previewCount == null || this.previewCount < 1) {
      return this.formControl.value;
    } else {
      this.remainingValueLength = this.formControl.value.length - this.previewCount;
      return this.formControl.value.slice(0, this.previewCount);
    }
  }

  onRemainingClick(): void {
    this.previewCount = null;
    this.remainingValueLength = 0;
  }
}
